import getFocusableElements from './get-focusable-elements.js';

class MainMenu {
  constructor(element, options = {
    debug: false,
  }) {
    this.nav = element;
    this.options = options;

    // Store DOM element references
    this.navBtns = [...this.nav.querySelectorAll('[data-ref="nav-btn"]')];
    this.primaryNav = this.nav.querySelector('.site-nav__list--primary');
    this.header = this.nav.closest('.site-header');
    this.subNavs = [...this.nav.querySelectorAll('[data-ref="main-menu-subnav"]')];

    // Prevent focus leak on subnav focusable elements
    this.preventFocusLeak();

    // Proxy callback on state changes.
    const self = this;

    this.state = new Proxy(
      {
        mobileNav: 'closed',
        initialized: false,
        openNav: null,
        event: null,
      },
      {
        set(state, key, value) {
          const oldValue = state[key];

          state[key] = value;

          if (key === 'openNav') {
            if (value === null) {
              if (self.options.debug) {
                // eslint-disable-next-line no-console
                console.log('all closed');
              }

              self.closeAll();
            } else if (oldValue !== value) {
              // nav item is new
              if (self.options.debug) {
                // eslint-disable-next-line no-console
                console.log('open new');
              }

              self.open();
            } else if (oldValue === value && state.event === 'click') {
              // nav item is same
              if (self.options.debug) {
                // eslint-disable-next-line no-console
                console.log('close current');
              }

              self.close();
            }
          }

          if (key === 'initialized' && oldValue !== value) {
            self.isInitialized();
          }

          if (key === 'mobileNav' && oldValue !== value) {
            self.updateMobileNav();
          }

          return state;
        },
      },
    );

    this.init();
  }

  init() {
    if (this.primaryNav) {
      this.renderBtn();
      this.bindFunctions();
      this.bindEvents();
      this.watchMedia();
      this.state.initialized = true;
    }
  }

  /**
   * Everything needed for the nav is set up.
   * Toggles attribute that can be used as style hook.
   */
  isInitialized() {
    this.nav.setAttribute('initialized', this.state.initialized ? 'true' : 'false');
  }

  /**
   * Get media query from :root style
   * @return {String} - media query string
   */
  setNavMq() {
    const rootStyles = getComputedStyle(document.documentElement);
    const mq = rootStyles.getPropertyValue('--nav-break');
    return `(max-width: ${mq})`;
  }

  /**
   * Set up matchMedia change event listener
   */
  watchMedia() {
    const query = this.setNavMq();
    const mediaQuery = window.matchMedia(query);

    // Call once to set initial state
    this.mediaChange(mediaQuery);

    mediaQuery.addEventListener('change', (event) => {
      this.mediaChange(event);
    });
  }

  /**
   * @param {Event} mobileMediaQuery - MediaQueryListEvent
   * Closes/hides nav if on mobile,
   * and removes or adds mouse events depending on viewports
   */
  mediaChange(mobileMediaQuery) {
    if (mobileMediaQuery.matches) {
      // Small screen
      this.resetNav();

      this.nav.removeEventListener('mouseover', this.onNavMouseover);
      this.nav.removeEventListener('mouseleave', this.onNavMouseleave);
    } else {
      // Large screen
      this.resetNav();

      this.nav.addEventListener('mouseover', this.onNavMouseover);
      this.nav.addEventListener('mouseleave', this.onNavMouseleave);
    }
  }

  /**
   * Reset nav buttons and hide subnavs.
   * Reset child focusable tabindex to prevent focus leaks
   */
  closeAll() {
    this.navBtns.forEach((element) => {
      element.setAttribute('aria-expanded', false);
    });

    this.subNavs.forEach((element) => {
      element.hidden = true;
      element.classList.remove('is-open');
      element.removeEventListener('keydown', this.onKeydown);
    });

    this.preventFocusLeak();
  }

  /**
   * Sets openNav to null.
   * Closes all active nav btns and open subnavs in the DOM
   */
  close() {
    this.state.openNav = null;

    this.closeAll();
  }

  /**
   * Opens new active sub nav
   */
  open() {
    this.closeAll();

    const activeBtn = this.navBtns.filter((element) => {
      return element.getAttribute('aria-controls') === this.state.openNav;
    });

    const activeSubNav = this.subNavs.filter((element) => {
      return element.getAttribute('id') === this.state.openNav;
    });

    activeBtn[0].setAttribute('aria-expanded', true);

    activeSubNav[0].hidden = false;
    activeSubNav[0].classList.add('is-open');

    this.enableFocus(activeSubNav[0]);
  }

  /**
   * Prevent focus from going into subnav while it is hidden
   */
  preventFocusLeak() {
    this.subNavs.forEach((element) => {
      const focusable = getFocusableElements(element);
      focusable.forEach((f) => {
        f.setAttribute('tabindex', '-1');
      });
    });
  }

  /**
   * Enable focus on a subnav's child elements when it is visible
   * @param {HTMLElement} subnav open subnav
   */
  enableFocus(subnav) {
    const focusableElements = getFocusableElements(subnav);
    focusableElements.forEach((element) => element.removeAttribute('tabindex'));
  }

  /**
   * Reset nav state and close all elements
   * Used when switching viewport experience
   */
  resetNav() {
    this.state.openNav = null;
    this.state.event = null;
    this.state.mobileNav = 'closed';

    this.closeAll();
  }

  /**
   * Showing/Hiding nav on mobile
   */
  updateMobileNav() {
    switch (this.state.mobileNav) {
    case 'closed':
      this.header.classList.remove('theme-dark');
      this.mobileMenuBtn.setAttribute('aria-expanded', 'false');
      this.mobileMenuBtn.setAttribute('aria-label', 'Open menu');
      this.mobileMenuBtn.textContent = 'Menu';

      document.documentElement.classList.remove('menu-active');
      document.documentElement.style.removeProperty('top');
      window.scrollTo(0, this.scrollPosition); // return to starting position
      document.documentElement.style.scrollBehavior = ''; // re-enable scroll behavior set in global css
      document.removeEventListener('keydown', this.onKeydown);
      this.close();

      break;
    case 'open':
      this.header.classList.add('theme-dark');
      this.mobileMenuBtn.setAttribute('aria-expanded', 'true');
      this.mobileMenuBtn.setAttribute('aria-label', 'Close menu');
      this.mobileMenuBtn.textContent = 'Close';

      this.scrollPosition = window.pageYOffset;
      document.documentElement.classList.add('menu-active');
      document.documentElement.style.top = `${-this.scrollPosition}px`; // maintain same position on page
      document.documentElement.style.scrollBehavior = 'auto'; // disable scroll animation for instant jump
      document.addEventListener('keydown', this.onKeydown);

      break;
    }
  }

  /**
   * Replace checkbox and label with button if JS is enabled
   */
  renderBtn() {
    document.querySelector('[data-ref="menu-checkbox"]').remove();
    document.querySelector('[data-ref="menu-label"]').remove();
    const btn = `<button
      class="sm-menu-btn btn"
      type="button"
      aria-controls="main-menu"
      aria-expanded="false"
      aria-label="open-menu"
      data-ref="mobile-menu-btn"
    >
      Menu
    </button>`;

    this.nav.insertAdjacentHTML('beforebegin', btn);

    this.mobileMenuBtn = document.querySelector('[data-ref="mobile-menu-btn"]');
  }

  /**
   * Bind functions to class so event listeners can be cleaned up
   */
  bindFunctions() {
    this.onNavMouseover = this.onNavMouseover.bind(this);
    this.onNavMouseleave = this.onNavMouseleave.bind(this);
    this.onNavClick = this.onNavClick.bind(this);
    this.onMobileMenuBtnClick = this.onMobileMenuBtnClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  /**
   * All event listeners except for the mouseover in mediaWatch
   */
  bindEvents() {
    this.nav.addEventListener('click', this.onNavClick);
    this.mobileMenuBtn.addEventListener('click', this.onMobileMenuBtnClick);

    document.addEventListener('focusin', () => {
      // Close subnav if it moves to a secondary nav element
      if (document.activeElement.dataset.ref === 'secondary-nav') {
        this.close();
      }

      // Close menu if focus moves to an element outside menu that isn't the menu button
      if (document.activeElement.dataset.ref !== 'mobile-menu-btn' && !this.nav.contains(document.activeElement)) {
        this.state.mobileNav = 'closed';
        this.close();
      }
    });
  }

  /**
   * Close mobile nav with escape key
   * @param {Event} event
   */
  onKeydown(event) {
    if (event.key === 'Escape') {
      this.state.mobileNav = 'closed';
    }
  }

  /**
   * Mouseover nav element and match relative child element targets
   * @param {Event} event
   */
  onNavMouseover(event) {
    // If mouseover secondary nav link or link in main nav, which would not trigger dropdown
    if (event.target.matches('[data-ref="secondary-nav"]') || event.target.closest('[data-ref="nav-link"]')) {
      this.state.event = 'mouseleave';
      this.state.openNav = null;
    }

    if (event.target.closest('[data-ref="nav-btn"]')) {
      this.state.event = 'mouseover';
      this.state.openNav = event.target.getAttribute('aria-controls');
    }
  }

  onNavMouseleave() {
    this.state.event = 'mouseleave';
    this.state.openNav = null;
  }

  /**
   * Click on nav button. Needed for mobile but also works on desktop
   * @param {Event} event
   */
  onNavClick(event) {
    if (event.target.closest('[data-ref="nav-btn"]')) {
      event.preventDefault();

      this.state.event = 'click';
      this.state.openNav = event.target.getAttribute('aria-controls');
    }
  }

  /**
   * Clicking on the mobile menu "burger"
   * @param {Event} event
   */
  onMobileMenuBtnClick() {
    this.state.mobileNav = this.state.mobileNav === 'closed' ? 'open' : 'closed';
  }
}

/**
 * Check for nav in DOM because some marketing
 * landing pages don't have a navigation.
 */
const menu = document.querySelector('[data-ref="main-menu"]');

if (menu) {
  new MainMenu(menu);
}
